import React, { useContext, useEffect, useState } from 'react'
import { Depths } from '@uifabric/fluent-theme'
import {
  ActionButton,
  getTheme,
  INavLink,
  INavLinkGroup,
  IRenderFunction,
  Nav,
  SearchBox,
  Separator,
  Stack,
} from 'office-ui-fabric-react'
import { SessionContext } from '../Contexts/SessionContext'
import { debounce } from 'lodash'

interface Props {
  minWidth: number | string
  selectedKey: string | undefined
  renderHeader: IRenderFunction<INavLinkGroup>
  groups: INavLinkGroup[]
  creationLabel: string
  onCreate?: () => void
}

export function FilterableCollectionSidebar({
  minWidth,
  selectedKey,
  renderHeader,
  groups,
  creationLabel,
  onCreate,
}: Props) {
  const [theme] = useState(getTheme())
  const sessionContext = useContext(SessionContext)
  const [filter, setFilter] = useState<string>('')
  const [filteredGroups, setFilteredGroups] = useState<INavLinkGroup[]>([])

  useEffect(() => {
    setFilteredGroups(
      groups.map((group, i) => ({
        ...group,
        links: [
          sessionContext.features.includes(
            'campaignsCollectionsSidebarFilter'
          ) && i === 0
            ? {
                name: 'filter',
              }
            : null,
          ...group.links.filter((link) => {
            const groupName = group.name && group.name.toLowerCase()
            const linkName = groupName + '/' + link.name.toLowerCase()

            return linkName.includes(filter) || link.url.includes(filter)
          }),
        ].filter(Boolean) as INavLink[],
      }))
    )
  }, [filter, groups, setFilter, setFilteredGroups, sessionContext.features])

  return (
    <Stack
      className="sidebar-collection"
      horizontalAlign="start"
      verticalAlign="start"
      verticalFill
      style={{
        minWidth: minWidth,
        boxShadow: Depths.depth8,
        paddingTop: 20,
        zIndex: 0,
        overflow: 'auto',
      }}
    >
      <Nav
        selectedKey={selectedKey}
        onRenderGroupHeader={renderHeader}
        onRenderLink={(
          props: INavLink | undefined,
          defaultRender: (
            props?: INavLink | undefined
          ) => JSX.Element | null = () => null
        ) =>
          props && props.name === 'filter' ? (
            <SearchBox
              autoFocus
              placeholder="Filter"
              iconProps={{ iconName: 'Filter' }}
              value={filter}
              spellCheck={false}
              disableAnimation={true}
              autoComplete={'off'}
              styles={{ root: { width: '100%' } }}
              onChange={debounce((value: string = '') => setFilter(value), 100)}
              onClear={() => setFilter('')}
            />
          ) : (
            defaultRender(props)
          )
        }
        styles={{
          root: {
            width: minWidth,
            borderLeft: 1,
            borderLeftColor: theme.palette.blackTranslucent40,
          },
          groupContent: {
            marginBottom: 0,
          },
          linkText: {
            fontStyle: 'italic',
            paddingRight: 20,
          },
        }}
        groups={filteredGroups}
      />
      {!!onCreate && (
        <>
          <Separator styles={{ root: { width: '100%' } }}></Separator>
          <Stack
            horizontalAlign="center"
            verticalAlign="start"
            horizontal
            style={{ width: '100%' }}
          >
            <ActionButton
              iconProps={{ iconName: 'Add' }}
              onClick={onCreate}
              text={creationLabel}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}
